import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { formatPrice } from '../../utils/money';

// Rendered inside <Elements> (which holds the clientSecret). Confirms the
// payment, then hands off to the confirmation page. It NEVER marks the order
// paid — the server webhook is authoritative.
export default function PaymentStep({ orderNumber, amountDue, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Used only for redirect-based methods (e.g. 3DS); card usually returns here.
        return_url: `${window.location.origin}/order/${orderNumber}`,
      },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message || 'Payment could not be completed.');
      setSubmitting(false);
      return;
    }

    // Payment submitted. The server confirms via webhook — the confirmation
    // page polls the order until it flips to "paid".
    navigate(`/order/${orderNumber}`);
  };

  return (
    <form className="ss-pay" onSubmit={onSubmit}>
      {error && <p className="ss-form__error" role="alert">{error}</p>}
      <PaymentElement />
      <div style={{ display: 'flex', gap: 'var(--ss-space-3)' }}>
        <Button type="submit" disabled={!stripe || submitting}>
          {submitting ? <Spinner label="Processing payment" /> : `Pay ${formatPrice(amountDue)}`}
        </Button>
        <Button type="button" variant="ghost" onClick={onBack} disabled={submitting}>
          Back
        </Button>
      </div>
      {process.env.REACT_APP_NODE_ENV !== 'production' && (
        <p style={{ color: 'var(--ss-brown-hover)', fontSize: '0.85rem' }}>
          Use Stripe test card <strong>4242 4242 4242 4242</strong>, any future date, any CVC.
        </p>
      )}
    </form>
  );
}
